import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  InputAdornment,
  Link as MuiLink,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Check,
  Close,
  Link as LinkIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useTenant } from '../providers/TenantProvider';

interface StripeAccount {
  id: string;
  email?: string;
  businessProfile?: string;
  country?: string;
  defaultCurrency?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  type?: string;
}

export const SettingsBillingPage = () => {
  const { tenantId } = useTenant();

  // Form state
  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [cancelCutoffHours, setCancelCutoffHours] = useState(24);
  const [rescheduleCutoffHours, setRescheduleCutoffHours] = useState(24);
  const [rateLimitMaxAttempts, setRateLimitMaxAttempts] = useState(10);
  const [rateLimitWindowMinutes, setRateLimitWindowMinutes] = useState(60);

  // UI state
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);
  const [checkingKeys, setCheckingKeys] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  // Verification state
  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [keysConfigured, setKeysConfigured] = useState(false);

  useEffect(() => {
    // Check if keys are already configured
    checkKeysConfigured();
    loadBookingSettings();
  }, [tenantId]);

  const loadBookingSettings = async () => {
    if (!tenantId) return;

    try {
      const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
      const settings = tenantSnap.data()?.bookingSettings || {};
      setCancelCutoffHours(settings.cancelCutoffHours ?? 24);
      setRescheduleCutoffHours(settings.rescheduleCutoffHours ?? 24);
      setRateLimitMaxAttempts(settings.publicRateLimit?.maxAttempts ?? 10);
      setRateLimitWindowMinutes(settings.publicRateLimit?.windowMinutes ?? 60);
    } catch (error) {
      console.error('Error loading booking settings:', error);
    }
  };

  const checkKeysConfigured = async () => {
    if (!tenantId) return;

    setCheckingKeys(true);
    try {
      const verifyKeys = httpsCallable(functions, 'verifyStripeKeys');
      const result: any = await verifyKeys({ tenantId });

      if (result.data.success) {
        setKeysConfigured(true);
        setStripeAccount(result.data.account);
      }
    } catch (error: any) {
      // Keys not configured yet, that's okay
      console.log('Keys not configured yet:', error.message);
      setKeysConfigured(false);
      setStripeAccount(null);
      // Don't show error message on initial load if keys aren't configured
    } finally {
      setCheckingKeys(false);
    }
  };

  const handleSaveKeys = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found' });
      return;
    }

    if (!publishableKey || !secretKey) {
      setMessage({ type: 'error', text: 'Please enter both publishable and secret keys' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const saveKeys = httpsCallable(functions, 'saveStripeKeys');
      const result: any = await saveKeys({
        tenantId,
        publishableKey,
        secretKey,
      });

      if (result.data.success) {
        setMessage({ type: 'success', text: 'Stripe keys saved securely!' });
        setPublishableKey('');
        setSecretKey('');
        setKeysConfigured(true);

        // Auto-verify after saving
        setTimeout(() => {
          handleVerifyKeys();
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error saving keys:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to save Stripe keys',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyKeys = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found' });
      return;
    }

    setVerifying(true);
    setMessage(null);

    try {
      const verifyKeys = httpsCallable(functions, 'verifyStripeKeys');
      const result: any = await verifyKeys({ tenantId });

      if (result.data.success) {
        setStripeAccount(result.data.account);
        setMessage({ type: 'success', text: 'Stripe keys verified successfully!' });
      }
    } catch (error: any) {
      console.error('Error verifying keys:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to verify Stripe keys',
      });
      setStripeAccount(null);
    } finally {
      setVerifying(false);
    }
  };

  const handleRegisterWebhook = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found' });
      return;
    }

    setRegistering(true);
    setMessage(null);

    try {
      // Get the cloud function URL for this tenant
      const functionUrl = `${window.location.origin.replace('5002', '5001')}/fitstac/us-central1/stripeWebhook`;
      const webhookUrlWithTenant = `${functionUrl}?tenantId=${tenantId}`;

      const registerWebhook = httpsCallable(functions, 'registerWebhook');
      const result: any = await registerWebhook({ 
        tenantId,
        url: webhookUrlWithTenant 
      });

      if (result.data.webhookId) {
        setWebhookUrl(webhookUrlWithTenant);
        setWebhookSecret(result.data.secret);
        setMessage({
          type: 'success',
          text: 'Webhook registered successfully! The webhook secret has been saved automatically.',
        });
      }
    } catch (error: any) {
      console.error('Error registering webhook:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to register webhook',
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleSaveBookingSettings = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found' });
      return;
    }

    setSavingBookingSettings(true);
    setMessage(null);

    try {
      await updateDoc(doc(db, 'tenants', tenantId), {
        bookingSettings: {
          cancelCutoffHours,
          rescheduleCutoffHours,
          publicRateLimit: {
            maxAttempts: rateLimitMaxAttempts,
            windowMinutes: rateLimitWindowMinutes,
          },
        },
        updatedAt: Timestamp.now(),
      });
      setMessage({ type: 'success', text: 'Booking settings saved.' });
    } catch (error: any) {
      console.error('Error saving booking settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save booking settings' });
    } finally {
      setSavingBookingSettings(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" gutterBottom>
          Settings → Billing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Configure Stripe API keys for payment processing. Keys are stored securely in Google
          Secret Manager and never saved as plaintext in the database.
        </Typography>

        {checkingKeys ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {message && (
              <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}

        {/* Stripe Keys Configuration */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Stripe API Keys
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get your API keys from{' '}
            <MuiLink href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener">
              Stripe Dashboard
            </MuiLink>
          </Typography>

          <TextField
            fullWidth
            label="Publishable Key"
            placeholder="pk_test_..."
            value={publishableKey}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setPublishableKey(e.target.value)}
            margin="normal"
            helperText="Starts with pk_test_ or pk_live_"
          />

          <TextField
            fullWidth
            label="Secret Key"
            placeholder="sk_test_..."
            type={showSecretKey ? 'text' : 'password'}
            value={secretKey}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSecretKey(e.target.value)}
            margin="normal"
            helperText="Starts with sk_test_ or sk_live_"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    edge="end"
                  >
                    {showSecretKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveKeys}
              disabled={loading || !publishableKey || !secretKey}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Keys'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleVerifyKeys}
              disabled={verifying || !keysConfigured}
            >
              {verifying ? <CircularProgress size={24} /> : 'Verify Keys'}
            </Button>
          </Box>
        </Paper>

        {/* Stripe Account Information */}
        {stripeAccount && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Connected Stripe Account
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Account ID
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {stripeAccount.id}
                  </Typography>
                </Box>

                {stripeAccount.email && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{stripeAccount.email}</Typography>
                  </Box>
                )}

                {stripeAccount.businessProfile && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Business Name
                    </Typography>
                    <Typography variant="body1">{stripeAccount.businessProfile}</Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {stripeAccount.chargesEnabled && (
                    <Chip label="Charges Enabled" color="success" icon={<Check />} />
                  )}
                  {stripeAccount.payoutsEnabled && (
                    <Chip label="Payouts Enabled" color="success" icon={<Check />} />
                  )}
                  {!stripeAccount.chargesEnabled && (
                    <Chip label="Charges Disabled" color="error" icon={<Close />} />
                  )}
                  <Chip
                    label={`${stripeAccount.country} · ${stripeAccount.defaultCurrency?.toUpperCase()}`}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Booking Policy */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Booking Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure public booking limits and customer cancel/reschedule cutoffs.
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth
              type="number"
              label="Cancel cutoff hours"
              value={cancelCutoffHours}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCancelCutoffHours(Math.max(0, parseInt(e.target.value) || 0))}
              helperText="Guests and members can cancel until this many hours before the appointment."
              inputProps={{ min: 0 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Reschedule cutoff hours"
              value={rescheduleCutoffHours}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRescheduleCutoffHours(Math.max(0, parseInt(e.target.value) || 0))}
              helperText="Guests and members can reschedule until this many hours before the appointment."
              inputProps={{ min: 0 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Public booking attempts per window"
              value={rateLimitMaxAttempts}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRateLimitMaxAttempts(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Public booking rate-limit window minutes"
              value={rateLimitWindowMinutes}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRateLimitWindowMinutes(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleSaveBookingSettings}
                disabled={savingBookingSettings}
              >
                {savingBookingSettings ? <CircularProgress size={24} /> : 'Save Booking Policy'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Webhook Configuration */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Webhook Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Register a webhook endpoint to receive real-time events from Stripe.
          </Typography>

          {webhookUrl && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Webhook URL:
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {webhookUrl}
              </Typography>
            </Alert>
          )}

          {webhookSecret && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Webhook Secret (saved automatically):
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {webhookSecret}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This secret has been saved securely. Use this secret in Stripe CLI or when testing webhooks.
              </Typography>
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={handleRegisterWebhook}
            disabled={registering || !keysConfigured}
            sx={{ mb: 3 }}
          >
            {registering ? <CircularProgress size={24} /> : 'Register Webhook'}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            💡 <Box component="strong" sx={{ fontWeight: 700 }}>For local testing:</Box> Use Stripe CLI to forward events:
            <Typography variant="body2" fontFamily="monospace" sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              stripe listen --forward-to {webhookUrl || 'YOUR_WEBHOOK_URL'}
            </Typography>
          </Typography>
        </Paper>

        {/* Security Notice */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            🔒 Security Notice
          </Typography>
          <Typography variant="body2">
            Your Stripe keys are stored securely in Google Secret Manager and are never saved as
            plaintext in the database. Only secret resource references are persisted in Firestore.
          </Typography>
        </Alert>
        </>
        )}
      </Container>
    </Box>
  );
};
